import DownloadIcon from "@mui/icons-material/Download";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  IconButton,
  FormControl,
  Popover,
  Radio,
  RadioGroup,
  Skeleton,
  Typography,
  useTheme,
  Stack,
  Select,
  MenuItem,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { PickersDay } from "@mui/x-date-pickers/PickersDay";
import dayjs from "dayjs";
import * as echarts from "echarts";
import axiosInstance from "../../axios/axios";
import { END_POINTS } from "../../shared";
import { toast } from "react-toastify";
import eventEmitter from "../../components/utils/eventEmitter";
import { useLocation } from "react-router-dom";
import { DatePicker } from "@mui/x-date-pickers";

type ComplianceTrendsProps = {
  height?: string | number;
};

const ComplianceTrendsSkeleton = () => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        bgcolor:
          theme.palette.mode === "dark"
            ? "#191C24"
            : "#FFFFFF",
        borderRadius: 2,
        p: 3,
        height: "100%",
        border: `1px solid ${theme.palette.mode === "dark"
          ? "rgba(255, 255, 255, 0.1)"
          : "rgba(0, 0, 0, 0.1)"
          }`,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Skeleton variant="text" width={200} height={32} />
        <Skeleton variant="circular" width={32} height={32} />
      </Box>

      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 2 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", px: 2 }}>
          {[1, 2, 3, 4, 5, 6].map((_, index) => (
            <Skeleton key={index} variant="text" width={40} height={20} />
          ))}
        </Box>

        <Box
          sx={{
            flex: 1,
            display: "flex",
            position: "relative",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundImage: `linear-gradient(to right, ${theme.palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.05)"
                : "rgba(0, 0, 0, 0.05)"
                } 1px, transparent 1px),
                            linear-gradient(to bottom, ${theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.05)"
                  : "rgba(0, 0, 0, 0.05)"
                } 1px, transparent 1px)`,
              backgroundSize: "20% 20%",
            },
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              p: 2,
            }}
          >
            {[1, 2, 3, 4].map((_, index) => (
              <Skeleton
                key={index}
                variant="rectangular"
                height={2}
                sx={{
                  width: "100%",
                  borderRadius: 1,
                  bgcolor:
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.1)"
                      : "rgba(0, 0, 0, 0.1)",
                }}
              />
            ))}
          </Box>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "center", gap: 4, mt: 2 }}>
          {[1, 2, 3, 4].map((_, index) => (
            <Box
              key={index}
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <Skeleton variant="circular" width={12} height={12} />
              <Skeleton variant="text" width={80} height={20} />
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

const ComplianceTrends: React.FC<ComplianceTrendsProps> = ({ height }) => {
  const location = useLocation();
  const [chartData, setChartData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const chartRef = useRef<HTMLDivElement>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const theme = useTheme();
  const [dateArray, setDateArray] = useState<any>([]);

  // Determine current route path
  const getCurrentRoute = () => {
    const pathname = location.pathname;
    if (pathname.includes('/compliance-dashboard')) {
      return 'compliance-dashboard';
    } else if (pathname.includes('/dashboard')) {
      return 'dashboard';
    }
    return 'dashboard'; // default
  };

  const currentRoute = getCurrentRoute();

  // Get path-specific localStorage keys
  const getStorageKey = (key: string, route?: string) => {
    const routeToUse = route || currentRoute;
    return `complianceTrends_${key}_${routeToUse}`;
  };

  // Initialize with saved values from localStorage if available
  const getSavedDatesForRoute = () => {
    try {
      const route = getCurrentRoute();
      const savedStartDate = localStorage.getItem(getStorageKey('startDate', route));
      const savedEndDate = localStorage.getItem(getStorageKey('endDate', route));
      const savedUserType = localStorage.getItem('dashboard-user-type');
      const currentLayout = localStorage.getItem('dashboard-layout');

      if (currentLayout && savedUserType && savedStartDate && savedEndDate) {
        return {
          start: dayjs(savedStartDate),
          end: dayjs(savedEndDate),
        };
      }
    } catch (e) {
      console.error("Error loading saved dates for Compliance Trends:", e);
    }
    return {
      start: dayjs().subtract(1, "year"),
      end: dayjs(),
    };
  };

  const initialDates = getSavedDatesForRoute();

  const [startDate, setStartDate] = useState<dayjs.Dayjs | null>(initialDates.start);
  const [endDate, setEndDate] = useState<dayjs.Dayjs | null>(initialDates.end);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [tempStartDate, setTempStartDate] = useState<dayjs.Dayjs | null>(null);
  const [tempEndDate, setTempEndDate] = useState<dayjs.Dayjs | null>(null);
  const [selectionStep, setSelectionStep] = useState<"start" | "end">("start");
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedFormat, setSelectedFormat] = useState("excel");
  const [trigger, setTrigger] = useState(0); // Used to re-trigger the effect

  const [timeRange, setTimeRange] = useState<'custom'>('custom');
  const [selectOpen, setSelectOpen] = useState(false);

  const [fromDate, setFromDate] = useState<dayjs.Dayjs | null>(initialDates.start);
  const [toDate, setToDate] = useState<dayjs.Dayjs | null>(initialDates.end);

  const [appliedFromDate, setAppliedFromDate] = useState<dayjs.Dayjs | null>(initialDates.start);
  const [appliedToDate, setAppliedToDate] = useState<dayjs.Dayjs | null>(initialDates.end);

  const [openFrom, setOpenFrom] = useState(false);
  const [openTo, setOpenTo] = useState(false);

  // Handle global filter changes
  useEffect(() => {
    const handleFilterSaved = () => {
      setLoading(true); // 🔥 Show skeleton loader immediately
      setTrigger((prev) => prev + 1); // Changing this will re-trigger the effect
    };

    eventEmitter.on("filterSaved", handleFilterSaved);

    return () => {
      eventEmitter.off("filterSaved", handleFilterSaved);
    };
  }, []);

  const FIXED_COLORS = [
    "#2E93FA",
    "#00E396",
    "#FF4560",
    "#775DD0",
    "#FFD700",
    "#FFA500",
  ];

  useEffect(() => {
    if (fromDate && toDate && fromDate.isAfter(toDate, "day")) {
      setFromDate(toDate);
    }
  }, [toDate]);

  const isInvalidRange =
    !fromDate || !toDate || fromDate.isAfter(toDate, "day");

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    setTempStartDate(startDate);
    setTempEndDate(endDate);
    setSelectionStep("start");
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSelectionStep("start");
  };

  const handleApply = () => {
    if (tempStartDate && tempEndDate) {
      setStartDate(tempStartDate);
      setEndDate(tempEndDate);
      setTimeRange('custom');
      handleClose();
    }
  };

  const handleDateSelect = (date: dayjs.Dayjs | null) => {
    if (selectionStep === "start") {
      // If previously chosen tempEndDate exists and is >= new start, keep it; otherwise clear it.
      const keepEnd =
        date && tempEndDate && !tempEndDate.isBefore(date, "day");

      setTempStartDate(date);
      setSelectionStep("end");
      setTempEndDate(keepEnd ? tempEndDate : null);
    } else {
      setTempEndDate(date);
    }
  };


  const open = Boolean(anchorEl);

  const formatDate = (dateString: string) => {
    return dayjs(dateString).format("MMM 'YY");
  };

  const handleDownload = () => {
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedFormat("excel");
  };

  const handleFormatChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedFormat(event.target.value);
  };

  const handleDownloadConfirm = async () => {
    try {
      setDownloadLoading(true);

      const payload = {
        duration: "custom_date_range",
        start_date: startDate?.format("YYYY-MM-DD") || "2024-05-01",
        end_date: endDate?.format("YYYY-MM-DD") || "2025-05-01",
        timezone: "Asia/Calcutta",
      };

      const response = await axiosInstance.post(
        "/securityscan/dashboard-compliance-trend",
        payload
      );

      if (response?.status === 200 && response?.data?.data?.graph_data) {
        const graphData = response.data.data.graph_data;

        if (selectedFormat === "CSV") {
          const excludedKeys = ["new_event", "device_count"];
          const headers = Object.keys(graphData[0]).filter(
            (key) => !excludedKeys.includes(key)
          );

          let csvContent = headers.join(",") + "\n";

          graphData.forEach((row: any) => {
            const rowData = headers.map((header) => {
              const value = row[header];
              if (header === "date_time") {
                return formatDate(value);
              }
              return typeof value === "number" ? Math.round(value) : value;
            });
            csvContent += rowData.join(",") + "\n";
          });

          const blob = new Blob([csvContent], {
            type: "text/csv;charset=utf-8;",
          });
          const link = document.createElement("a");
          const url = URL.createObjectURL(blob);
          link.setAttribute("href", url);
          link.setAttribute(
            "download",
            `compliance_trends_${startDate?.format(
              "YYYY-MM-DD"
            )}_to_${endDate?.format("YYYY-MM-DD")}.csv`
          );
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        } else if (selectedFormat === "PDF") {
          // TODO: Implement PDF download logic here
          console.log("PDF download not implemented yet");
        }

        toast.success("Download completed successfully!", {
          position: "bottom-left",
          autoClose: 3000,
        });
      }
    } catch (error: any) {
      console.error("Error downloading compliance trend data:", error);
      toast.error("Failed to download compliance trend data", {
        position: "top-right",
        autoClose: 5000,
      });
    } finally {
      setDownloadLoading(false);
      setDialogOpen(false);
    }
  };

  const handleDownloadReport = async () => {
    try {
      setDownloadLoading(true);
      const payload = {
        report_format: selectedFormat.toLowerCase(),
        time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        type: "org_compliance",
        asset_ids: "1",
        // report_name:"Organisation Compliance Report",
        // module:"on_premise",
        // is_scheduled:false,
        // frequenct_name:"",
        // description:"",
        // customise_asset_ids:[],

        // report_metadata: {
        //   asset_compliance: {
        //     compliance_description: false,
        //     frameworks: ["NIST_800-171 r2", "NIST_800-53 r5", "CIS_8", "PCI DSS_4.0.1", "SPARK_1"]
        //   }
        // }
      };
      const response = await axiosInstance.post(END_POINTS.REPORT_DOWNLOAD, payload);
      if (response.status === 200) {
        toast.success("Report request submitted, please check Reports section.");
      }
      setDialogOpen(false);
    } catch (error) {
      console.error('Error making API call:', error);
      toast.error("Failed to submit report request, please try again.");
    } finally {
      setDownloadLoading(false);
      setDialogOpen(false);
    }
  };

  useEffect(() => {
    fetchComplianceData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, trigger]);

  const fetchComplianceData = async () => {
    setLoading(true); // 🔥 Always show skeleton loader
    try {
      if (!startDate) {
        setStartDate(dayjs("2024-05-01"));
      }
      if (!endDate) {
        setEndDate(dayjs("2025-05-01"));
      }

      const payload = {
        duration: "custom_date_range",
        start_date: startDate?.format("YYYY-MM-DD") || "2024-05-01",
        end_date: endDate?.format("YYYY-MM-DD") || "2025-05-01",
        timezone: "Asia/Calcutta",
      };

      const response = await axiosInstance.post(
        "/securityscan/dashboard-compliance-trend",
        payload
      );

      if (response?.status === 200 && response?.data?.data?.graph_data) {
        const graphData = response.data.data.graph_data;
        const seriesMap: Record<string, number[]> = {};
        const excludedKeys = ["date_time", "new_event", "device_count"];

        const allKeys = new Set<string>();
        graphData.forEach((item: any) => {
          Object.keys(item).forEach((key) => {
            if (!excludedKeys.includes(key)) {
              allKeys.add(key);
            }
          });
        });

        allKeys.forEach((key) => {
          seriesMap[key] = new Array(graphData.length).fill(0);
        });

        graphData.forEach((item: any, index: number) => {
          Object.entries(item).forEach(([key, value]) => {
            if (!excludedKeys.includes(key)) {
              seriesMap[key][index] = Math.round(Number(value));
            }
          });
        });

        // Use time-series data points so echarts can render a proper time axis
        const result = Array.from(allKeys).map((key, index) => ({
          name: key.replace(/_/g, " "),
          type: "line",
          smooth: true,
          // Hide individual point markers for a cleaner line look,
          // keep a reasonable symbol size for hover state.
          showSymbol: false,
          symbol: "circle",
          symbolSize: 6,
          lineStyle: {
            width: 2,
          },
          // each data point is [time, value]
          data: graphData.map((item: any) => [item.date_time, Math.round(Number(item[key] || 0))]),
          color: FIXED_COLORS[index % FIXED_COLORS.length],
        }));

        // Keep raw date values array for other uses (not required by time xAxis)
        const rawDates = graphData.map((item: any) => item.date_time);
        setDateArray(rawDates);
        setChartData(result);
      }
    } catch (error: any) {
      console.error(
        "Error fetching compliance trend data:",
        error?.response?.data || error
      );
      setChartData([]);
      setDateArray([]);
      toast.error(
        error?.response?.data?.message ||
        "Failed to fetch compliance trend data",
        {
          position: "top-right",
          autoClose: 5000,
        }
      );
    } finally {
      setLoading(false); // ✅ Always hide skeleton after data loads
    }
  };

  const options = {
    tooltip: {
      trigger: "axis",
      confine: true,
      formatter: function (params: any) {
        if (params && params.length > 0) {
          const date = params[0].axisValue;
          const formatted = dayjs(date).format("DD/MMM/YY");
          let tooltip = `<div style='margin-bottom: 4px; font-weight: 600;'>${formatted}</div>`;
          params.forEach((item: any) => {
            tooltip += `<div style='display: flex; align-items: center; gap: 8px;'><span style='display:inline-block;width:10px;height:10px;background:${item.color};border-radius:50%;margin-right:6px;'></span>${item.seriesName} <b style='margin-left:4px;'>${item.data}</b></div>`;
          });
          return tooltip;
        }
        return "";
      },
    },
    legend: {
      show: true,
      bottom: 0,
      type: "scroll",
      padding: [0, 20],
      textStyle: {
        color: theme.palette.mode === "dark" ? "#fff" : "#374151",
      },
    },
    grid: {
      top: "3%",
      right: "3%",
      bottom: "15%",
      left: "3%",
      containLabel: true,
    },
    xAxis: {
      type: "time",
      axisLabel: {
        margin: 15,
        formatter: (value: string) => formatDate(value),
        // Force every label to show (no skipping). If labels overlap,
        // consider rotating via `rotate` or reducing font size.
        interval: 0,
        // Turn off automatic hiding since we want a fixed interval
        hideOverlap: false,
        overflow: "break",
        color: theme.palette.mode === "dark" ? "#fff" : "#374151",
      },
      axisTick: {
        alignWithLabel: true,
      },
    },
    yAxis: {
      type: "value",
      axisLabel: {
        margin: 10,
        formatter: "{value}%",
        color: theme.palette.mode === "dark" ? "#fff" : "#374151",
      },
      splitLine: {
        show: true,
        lineStyle: {
          color: theme.palette.mode === "dark" ? "#6D738F" : "#B4C3D1",
          type: "solid",
        },
      },
    },
    series: chartData,
  };

  useEffect(() => {
    if (chartRef.current && chartData.length > 0 && dateArray.length > 0) {
      // Dispose existing instance if any
      const existingInstance = echarts.getInstanceByDom(chartRef.current);
      if (existingInstance) {
        existingInstance.dispose();
      }

      // Create new instance
      const chartInstance = echarts.init(chartRef.current);
      chartInstance.setOption({
        ...options,
        grid: {
          ...options.grid,
          containLabel: true,
        },
      });

      const handleResize = () => {
        if (chartInstance && !chartInstance.isDisposed()) {
          chartInstance.resize();
          chartInstance.setOption({
            xAxis: {
              ...options.xAxis,
              interval: "auto",
            },
          });
        }
      };

      // Listen to both window resize and custom resize events
      window.addEventListener("resize", handleResize);

      // Add ResizeObserver to handle container resize (for grid layout changes)
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }

      resizeObserverRef.current = new ResizeObserver(() => {
        handleResize();
      });

      if (chartRef.current) {
        resizeObserverRef.current.observe(chartRef.current);
      }

      // Trigger initial resize to ensure proper rendering
      setTimeout(() => {
        handleResize();
      }, 100);

      return () => {
        window.removeEventListener("resize", handleResize);
        if (resizeObserverRef.current) {
          resizeObserverRef.current.disconnect();
        }
        if (chartInstance && !chartInstance.isDisposed()) {
          chartInstance.dispose();
        }
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartData, dateArray, theme.palette.mode]);

  const CustomDay = (props: any) => {
    const { day, ...other } = props;
    const isInRange =
      tempStartDate &&
      tempEndDate &&
      day.isAfter(tempStartDate, "day") &&
      day.isBefore(tempEndDate, "day");

    const isSelected =
      (tempStartDate && day.isSame(tempStartDate, "day")) ||
      (tempEndDate && day.isSame(tempEndDate, "day"));

    return (
      <PickersDay
        {...other}
        day={day}
        sx={{
          ...(isInRange && {
            backgroundColor: `${theme.palette.primary.main}33`,
            color: theme.palette.text.primary,
            "&:hover": {
              backgroundColor: `${theme.palette.primary.main}4D`,
            },
          }),
          ...(isSelected && {
            backgroundColor: `${theme.palette.primary.main} !important`,
            color: `${theme.palette.common.white} !important`,
            opacity: "1 !important",
            fontWeight: "bold",
            "&:hover": {
              backgroundColor: `${theme.palette.primary.dark} !important`,
            },
          }),
        }}
      />
    );
  };

  // Sync dates when route changes
  useEffect(() => {
    const route = getCurrentRoute();
    const savedStartDate = localStorage.getItem(getStorageKey('startDate', route));
    const savedEndDate = localStorage.getItem(getStorageKey('endDate', route));
    const savedUserType = localStorage.getItem('dashboard-user-type');
    const currentLayout = localStorage.getItem('dashboard-layout');

    let newStart = dayjs().subtract(1, "year");
    let newEnd = dayjs();

    if (currentLayout && savedUserType && savedStartDate && savedEndDate) {
      newStart = dayjs(savedStartDate);
      newEnd = dayjs(savedEndDate);
    }

    setStartDate(newStart);
    setEndDate(newEnd);
    setAppliedFromDate(newStart);
    setAppliedToDate(newEnd);
    setFromDate(newStart);
    setToDate(newEnd);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    if (startDate) {
      localStorage.setItem(getStorageKey('startDate'), startDate.format('YYYY-MM-DD'));
    }
    if (endDate) {
      localStorage.setItem(getStorageKey('endDate'), endDate.format('YYYY-MM-DD'));
    }
    setAppliedFromDate(startDate);
    setAppliedToDate(endDate);
    setFromDate(startDate);
    setToDate(endDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, currentRoute]);

  const getTimeRangeDisplayText = (): string => {
    if (appliedFromDate && appliedToDate) {
      return `${appliedFromDate.format("DD MMM YYYY")} - ${appliedToDate.format("DD MMM YYYY")}`;
    }
    return "Select date range";
  };

  return (
    <Box
      sx={{
        bgcolor:
          theme.palette.mode === "dark"
            ? "#191C24"
            : "#FFFFFF",
        borderRadius: 2,
        p: 1,
        pl: 2,
        height: height || "100%",
        border: `1px solid ${theme.palette.mode === "dark"
          ? "rgba(255, 255, 255, 0.1)"
          : "rgba(0, 0, 0, 0.1)"
          }`,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.mode === "dark" ? "#fff" : "inherit", fontSize: 14,
            fontWeight: 600,
          }}
        >
          COMPLIANCE TRENDS
        </Typography>
        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }} className="no-drag">
          <FormControl size="small" sx={{ minWidth: 190 }}>
            <Select
              open={selectOpen}
              onOpen={() => {
                setFromDate(appliedFromDate);
                setToDate(appliedToDate);
                setSelectOpen(true);
              }}
              onClose={() => {
                // 🔥 RESET if Apply not clicked
                setFromDate(appliedFromDate);
                setToDate(appliedToDate);
                setSelectOpen(false);
              }}
              value={timeRange}
              renderValue={() => getTimeRangeDisplayText()}
              sx={{
                fontSize: '12px',
                fontWeight: 700,
                height: '32px',
              }}
            >
              {/* ✅ ONE MenuItem containing custom UI */}
              <MenuItem disableRipple disableTouchRipple>
                <Box onClick={(e) => e.stopPropagation()}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <Stack spacing={2} width={500}>
                      <Box display="flex" gap={2}>
                        <DatePicker
                          label="From"
                          value={fromDate}
                          maxDate={toDate || dayjs()}
                          onChange={setFromDate}
                          slotProps={{ textField: { size: "small" } }}
                          open={openFrom}
                          onOpen={() => {
                            setOpenFrom(true);
                            setOpenTo(false);   // 🔥 close To picker
                          }}
                          onClose={() => setOpenFrom(false)}
                        />

                        <DatePicker
                          label="To"
                          value={toDate}
                          minDate={fromDate || undefined}
                          maxDate={dayjs()}
                          onChange={setToDate}
                          open={openTo}
                          onOpen={() => {
                            setOpenTo(true);
                            setOpenFrom(false); // 🔥 close From picker
                          }}
                          onClose={() => setOpenTo(false)}
                          slotProps={{ textField: { size: "small" } }}
                        />

                        <Button
                          variant="contained"
                          size="small"
                          disabled={isInvalidRange}
                          onClick={() => {
                            setAppliedFromDate(fromDate);
                            setAppliedToDate(toDate);
                            setStartDate(fromDate);
                            setEndDate(toDate);
                            setSelectOpen(false);
                          }}
                          sx={{ textTransform: "none", fontWeight: 600 }}
                        >
                          Apply
                        </Button>
                      </Box>
                    </Stack>
                  </LocalizationProvider>
                </Box>
              </MenuItem>
            </Select>
          </FormControl>
          <IconButton
            size="small"
            onClick={handleDownload}
            disabled={downloadLoading || loading}
            className="no-drag clickable-area"
            sx={{ cursor: "pointer" }}
          >
            {downloadLoading ? (
              <CircularProgress
                size={20}
                sx={{
                  color: theme.palette.mode === "dark" ? "#B4C3D1" : "#2563EB",
                }}
              />
            ) : (
              <DownloadIcon
                sx={{
                  fontSize: 20,
                }}
              />
            )}
          </IconButton>
        </Box>
      </Box>

      {loading ? (
        <ComplianceTrendsSkeleton />
      ) : (
        <Box ref={chartRef} sx={{ flex: 1 }} className="no-drag clickable-area" style={{ cursor: 'pointer' }} />
      )}

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        PaperProps={{
          sx: {
            boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
            borderRadius: "8px",
          },
        }}
      >
        <Box sx={{ p: 2 }} className="no-drag clickable-area">
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {selectionStep === "start"
              ? "Select start date"
              : "Select end date"}
          </Typography>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DateCalendar
              value={selectionStep === "start" ? tempStartDate : tempEndDate}
              onChange={handleDateSelect}
              maxDate={dayjs()}
              minDate={
                selectionStep === "end"
                  ? tempStartDate || undefined  // allow same date
                  : undefined
              }
              views={["day"]}
              slots={{
                day: CustomDay,
              }}
            />
          </LocalizationProvider>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              borderTop: 1,
              borderColor: "divider",
              pt: 2,
              mt: 2,
            }}
            className="no-drag"
          >
            <Button
              onClick={handleApply}
              variant="contained"
              disabled={
                !tempStartDate ||
                !tempEndDate ||
                tempEndDate.isBefore(tempStartDate, "day") ||
                (!!startDate &&
                  !!endDate &&
                  tempStartDate.isSame(startDate, "day") &&
                  tempEndDate.isSame(endDate, "day"))
              }
              className="no-drag clickable-area"
              sx={{
                textTransform: "none",
                px: 3,
                cursor: "pointer",
              }}
            >
              Apply
            </Button>
          </Box>
        </Box>
      </Popover>

      <Dialog
        open={dialogOpen}
        onClose={handleDialogClose}
        className="no-drag clickable-area"
        sx={{
          "& .MuiDialog-paper": {
            position: "absolute",
            top: "50%",
            left: "50%",
            width: "360px",
            height: "300px",
            borderRadius: "4px",
            transform: "translate(-50%, -50%)",
            padding: "10px",
            boxSizing: "border-box",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            textAlign: "center",
            fontWeight: "bold",
            fontSize: "16px",
            color: theme.palette.mode === "dark" ? "#ffffff" : "#101828",
          }}
        >
          Download Org Compliance Report?
        </DialogTitle>
        <DialogContent
          sx={{
            textAlign: "center",
            fontWeight: 400,
            fontSize: "14px",
            color: theme.palette.mode === "dark" ? "#b0b0b0" : "#667085",
            overflow: "visible",
            maxHeight: "none",
          }}
        >
          <Typography variant="body2" sx={{ mb: 2, color: theme.palette.mode === "dark" ? "#b0b0b0" : "#666666" }}>
            This report offers a comprehensive summary of your Organization Compliance data
          </Typography>
          <Typography
            variant="body2"
            fontWeight={500}
            align="center"
            sx={{ mb: 2, fontWeight: "bold" }}
          >
            Report Format
          </Typography>
          <RadioGroup
            value={selectedFormat}
            onChange={handleFormatChange}
            row
            sx={{ justifyContent: "center" }}
          >
            <FormControlLabel value="excel" control={<Radio />} label="Excel" />
            <FormControlLabel value="pdf" control={<Radio />} label="PDF" />
          </RadioGroup>
        </DialogContent>
        <DialogActions
          sx={{
            display: "flex",
            justifyContent: "center",
            gap: "12px",
            paddingBottom: "24px",
          }}
          className="no-drag"
        >
          <Button
            onClick={handleDialogClose}
            variant="outlined"
            className="no-drag clickable-area"
            sx={{ width: 130, cursor: "pointer" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDownloadReport}
            variant="contained"
            color="primary"
            disabled={downloadLoading || !selectedFormat}
            className="no-drag clickable-area"
            sx={{ width: 130, cursor: "pointer" }}
          >
            {downloadLoading ? (
              <Box display="flex" alignItems="center" gap={1}>
                <CircularProgress size={10} sx={{ color: 'white' }} />
                <Typography sx={{ color: 'white', fontSize: '11px' }}>
                  Downloading...
                </Typography>
              </Box>
            ) : (
              "Download"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ComplianceTrends;
